import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button, Card, Input, Label } from '../design-system';
import { validateResetToken, resetPassword } from '../services/consentApi';

interface PasswordStrength {
  score: number; // 0-4
  label: string;
  color: string;
}

/**
 * Reset Password page - allows users to set a new password using a valid token.
 * Route: /reset-password?token=xxx
 */
const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setValidating(false);
        setError('No reset token provided. Please request a new password reset link.');
        return;
      }

      try {
        const result = await validateResetToken(token);
        setTokenValid(result.valid);
        if (result.email) {
          setUserEmail(result.email);
        }
        if (!result.valid) {
          setError(result.message || 'This reset link is invalid or has expired. Please request a new one.');
        }
      } catch (err: any) {
        setTokenValid(false);
        setError(err.response?.data?.message || 'Failed to validate reset token. Please request a new password reset link.');
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  // Password strength calculator
  const passwordStrength = useMemo((): PasswordStrength => {
    if (!password) {
      return { score: 0, label: '', color: 'transparent' };
    }

    let score = 0;
    
    // Length checks
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    
    // Character type checks
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;

    // Map to 0-4 scale
    const mappedScore = Math.min(4, Math.floor(score * 0.7));

    const labels = ['Very Weak', 'Weak', 'Fair', 'Strong', 'Very Strong'];
    const colors = ['#dc2626', '#f97316', '#eab308', '#22c55e', '#16a34a'];

    return {
      score: mappedScore,
      label: labels[mappedScore],
      color: colors[mappedScore],
    };
  }, [password]);

  // Password validation rules
  const passwordRules = useMemo(() => {
    return {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasDigit: /\d/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
  }, [password]);

  const allRulesPassed = Object.values(passwordRules).every(Boolean);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!password) {
      setError('Password is required');
      return;
    }

    if (!allRulesPassed) {
      setError('Password does not meet all requirements');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await resetPassword({
        token,
        password,
        confirmPassword,
      });
      setSuccess(true);
    } catch (err: any) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (validating) {
    return (
      <main className="ds-container" aria-labelledby="reset-password-title">
        <div style={{ maxWidth: 480, margin: '0 auto' }}>
          <Card>
            <div className="ds-card__body" style={{ textAlign: 'center', padding: '48px 24px' }}>
              <div className="ds-spinner" aria-hidden="true" style={{ 
                width: 40, 
                height: 40, 
                border: '3px solid var(--ds-border)', 
                borderTopColor: 'var(--ds-primary)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 16px'
              }} />
              <p className="ds-text-muted">Validating reset link...</p>
            </div>
          </Card>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </main>
    );
  }

  // Invalid token state
  if (!tokenValid && !success) {
    return (
      <main className="ds-container" aria-labelledby="reset-password-title">
        <div style={{ maxWidth: 480, margin: '0 auto' }}>
          <Card>
            <header className="ds-card__header" style={{ alignItems: 'flex-start' }}>
              <div>
                <p className="ds-text-muted" style={{ margin: 0, fontWeight: 600 }}>Consent Platform</p>
                <h1 id="reset-password-title" className="ds-heading-xl">Invalid Reset Link</h1>
              </div>
            </header>

            <div className="ds-card__body">
              <div className="ds-alert ds-alert--error" role="alert">
                {error || 'This password reset link is invalid or has expired.'}
              </div>
              <div style={{ marginTop: 24 }}>
                <Link to="/forgot-password">
                  <Button variant="primary">Request New Reset Link</Button>
                </Link>
              </div>
            </div>

            <footer className="ds-card__footer">
              <p className="ds-text-muted" style={{ margin: 0, textAlign: 'center' }}>
                <Link to="/login" style={{ color: 'var(--ds-accent)' }}>
                  ← Back to Sign In
                </Link>
              </p>
            </footer>
          </Card>
        </div>
      </main>
    );
  }

  // Success state
  if (success) {
    return (
      <main className="ds-container" aria-labelledby="reset-password-title">
        <div style={{ maxWidth: 480, margin: '0 auto' }}>
          <Card>
            <header className="ds-card__header" style={{ alignItems: 'flex-start' }}>
              <div>
                <p className="ds-text-muted" style={{ margin: 0, fontWeight: 600 }}>Consent Platform</p>
                <h1 id="reset-password-title" className="ds-heading-xl">Password Reset Complete</h1>
              </div>
            </header>

            <div className="ds-card__body">
              <div className="ds-alert ds-alert--success" role="status">
                <strong>Your password has been reset successfully!</strong>
                <p style={{ margin: '8px 0 0' }}>
                  You can now sign in with your new password.
                </p>
              </div>
              <div style={{ marginTop: 24 }}>
                <Button variant="primary" onClick={() => navigate('/login')}>
                  Go to Sign In
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </main>
    );
  }

  // Reset form
  return (
    <main className="ds-container" aria-labelledby="reset-password-title">
      <div style={{ maxWidth: 480, margin: '0 auto' }}>
        <Card>
          <header className="ds-card__header" style={{ alignItems: 'flex-start' }}>
            <div>
              <p className="ds-text-muted" style={{ margin: 0, fontWeight: 600 }}>Consent Platform</p>
              <h1 id="reset-password-title" className="ds-heading-xl">Reset your password</h1>
              {userEmail && (
                <p className="ds-text-muted" style={{ marginTop: 6 }}>
                  Enter a new password for <strong>{userEmail}</strong>
                </p>
              )}
            </div>
          </header>

          <form className="ds-form" onSubmit={handleSubmit} noValidate aria-describedby={error ? 'reset-error' : undefined}>
            {error && (
              <div className="ds-alert ds-alert--error" id="reset-error" role="alert">
                {error}
              </div>
            )}

            <div className="ds-field">
              <Label htmlFor="password">New Password</Label>
              <Input
                type="password"
                id="password"
                name="password"
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setPassword(e.target.value)}
                placeholder="Enter new password"
                required
                autoComplete="new-password"
                autoFocus
              />
              
              {/* Password strength indicator */}
              {password && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ 
                    display: 'flex', 
                    gap: 4, 
                    marginBottom: 4 
                  }}>
                    {[0, 1, 2, 3].map((index) => (
                      <div
                        key={index}
                        style={{
                          flex: 1,
                          height: 4,
                          borderRadius: 2,
                          backgroundColor: index <= passwordStrength.score 
                            ? passwordStrength.color 
                            : 'var(--ds-border)',
                          transition: 'background-color 0.2s',
                        }}
                      />
                    ))}
                  </div>
                  <span style={{ 
                    fontSize: 12, 
                    color: passwordStrength.color,
                    fontWeight: 500,
                  }}>
                    {passwordStrength.label}
                  </span>
                </div>
              )}

              {/* Password requirements */}
              <ul style={{ 
                margin: '12px 0 0', 
                padding: 0, 
                listStyle: 'none',
                fontSize: 12,
              }}>
                {[
                  { key: 'minLength', label: 'At least 8 characters' },
                  { key: 'hasUppercase', label: 'One uppercase letter' },
                  { key: 'hasLowercase', label: 'One lowercase letter' },
                  { key: 'hasDigit', label: 'One number' },
                  { key: 'hasSpecial', label: 'One special character (!@#$%^&*...)' },
                ].map(({ key, label }) => (
                  <li
                    key={key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      marginBottom: 4,
                      color: passwordRules[key as keyof typeof passwordRules] 
                        ? 'var(--ds-success, #22c55e)' 
                        : 'var(--ds-text-muted)',
                    }}
                  >
                    <span style={{ fontSize: 14 }}>
                      {passwordRules[key as keyof typeof passwordRules] ? '✓' : '○'}
                    </span>
                    {label}
                  </li>
                ))}
              </ul>
            </div>

            <div className="ds-field">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={confirmPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                autoComplete="new-password"
              />
              {confirmPassword && password !== confirmPassword && (
                <span style={{ color: 'var(--ds-error, #dc2626)', fontSize: 12, marginTop: 4, display: 'block' }}>
                  Passwords do not match
                </span>
              )}
              {confirmPassword && password === confirmPassword && confirmPassword.length > 0 && (
                <span style={{ color: 'var(--ds-success, #22c55e)', fontSize: 12, marginTop: 4, display: 'block' }}>
                  ✓ Passwords match
                </span>
              )}
            </div>

            <Button 
              type="submit" 
              variant="primary" 
              isLoading={loading} 
              disabled={loading || !allRulesPassed || password !== confirmPassword}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>

          <footer className="ds-card__footer">
            <p className="ds-text-muted" style={{ margin: 0, textAlign: 'center' }}>
              <Link to="/login" style={{ color: 'var(--ds-accent)' }}>
                ← Back to Sign In
              </Link>
            </p>
          </footer>
        </Card>
      </div>
    </main>
  );
};

export default ResetPassword;
